import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ERROR_GENERIC, MERCADO_ADS_AUTH_URL, MERCADO_ADS_SLUG, authUrlOf, errorCodeOf} from './mercadoAds';

/**
 * Las llamadas de Mercado Ads de la agencia y del Portal (contrato §3). La URL de autorización la arma
 * el backend con el `state` firmado: el Frontend no conoce ni el `client_id` ni la redirect.
 *
 * Resuelven `{ok: true}` cuando ya se fue (o se va) a Mercado Libre, y `{ok: false, code}` si no: el
 * `code` es uno de los de `mercadoAds.js` y la pantalla lo dibuja donde se pidió.
 */

const PORTAL_BASE = API_URL + 'portal/me/connections/' + MERCADO_ADS_SLUG + '/';

function goTo(url) {
    window.location.assign(url);
}

// El cliente activo del Portal viaja de la pantalla a la vuelta de Mercado Libre: el callback se monta
// fuera del marco del Portal y no tiene su contexto. El `state` también lo lleva; esto es para que el
// backend lo reciba explícito, como en los otros slugs (con N clientes lo exige).
const PORTAL_CLIENT_KEY = 'dashbo.mercado_ads.portal_client';

function rememberPortalClient(activeClientId) {
    try {
        if (activeClientId) window.sessionStorage.setItem(PORTAL_CLIENT_KEY, String(activeClientId));
        else window.sessionStorage.removeItem(PORTAL_CLIENT_KEY);
    } catch (e) {
        // Sin almacenamiento, el cliente sale del `state`.
    }
}

function consumePortalClient() {
    try {
        const value = window.sessionStorage.getItem(PORTAL_CLIENT_KEY);
        window.sessionStorage.removeItem(PORTAL_CLIENT_KEY);
        return value || null;
    } catch (e) {
        return null;
    }
}

function startAuth(url, navigate) {
    return new APIClient(url).call('GET')
        .then((response) => {
            const authUrl = authUrlOf(response);
            // Sin `status` es la forma de `get_auth_url/dv360/` (`{auth_url}` pelado); con `status`,
            // tiene que ser OK: un código tipado nunca trae una URL a la que ir.
            if (authUrl && (!response.status || response.status === 'OK')) {
                navigate(authUrl);
                return {ok: true};
            }
            return {ok: false, code: errorCodeOf(response)};
        })
        .catch(() => ({ok: false, code: ERROR_GENERIC}));
}

/** «Conectar con la cuenta principal del vendedor», «Probar con otra cuenta» y «Reconectar». */
export function startMercadoAdsAgencyAuth({navigate = goTo} = {}) {
    return startAuth(API_URL + MERCADO_ADS_AUTH_URL, navigate);
}

/**
 * El mismo OAuth desde el Portal, para el cliente activo. El parámetro del cliente va con los dos
 * nombres: `client_id` es el del contrato y `agency_client_id` el que usan los otros slugs del Portal.
 */
export function startMercadoAdsPortalAuth(activeClientId, {navigate = goTo} = {}) {
    const params = new URLSearchParams();
    if (activeClientId) {
        params.set('client_id', activeClientId);
        params.set('agency_client_id', activeClientId);
    }
    const query = params.toString();
    rememberPortalClient(activeClientId);
    return startAuth(PORTAL_BASE + 'auth-url/' + (query ? '?' + query : ''), navigate);
}

/**
 * La vuelta del OAuth en el Portal: `{code, state}` → credencial, anunciantes y si hay que elegir. Sin
 * cliente explícito, el que dejó anotado `startMercadoAdsPortalAuth`.
 */
export function postMercadoAdsPortalCallback(code, state, activeClientId = null) {
    const payload = {code, state};
    const clientId = activeClientId || consumePortalClient();
    if (clientId) payload.agency_client_id = clientId;
    return new APIClient(PORTAL_BASE + 'callback/').call('POST', payload);
}

/**
 * Los anunciantes que el cliente comparte con su agencia, cuando tiene más de uno. El backend toma
 * `client_id` y la credencial que acaba de conectar (`credential_id`).
 */
export function selectMercadoAdsAdvertisers(advertiserIds, activeClientId, credentialId = null) {
    const payload = {advertiser_ids: advertiserIds};
    if (activeClientId) payload.client_id = activeClientId;
    if (credentialId) payload.credential_id = credentialId;
    return new APIClient(PORTAL_BASE + 'select/').call('POST', payload);
}
